import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  forgotPassword,
  getGoogleOAuthUrl,
  getUserInfo,
  login,
  register,
  sendCaptcha,
  validateRegisterInviteCode,
  type RegisterResult,
  type UserInfo,
} from "@/lib/api/auth";
import { saveToken, saveUserInfo } from "@/lib/utils/auth-storage";
import {
  clearInviteCode,
  getInviteCode,
  saveInviteCode,
  normalizeInviteCode,
} from "@/lib/utils/invite-code";
import { encryptPassword } from "@/lib/utils/rsa";
import { Eye, EyeOff } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

type Mode = "signin" | "signup" | "forgot";

export interface LoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with fresh user info after successful login or signup. */
  onSuccess?: (user: UserInfo) => void;
  initialMode?: Mode;
}

/**
 * Login / Signup / Forgot-password dialog.
 *
 * Extracted verbatim (UI + handlers) from the pre-Landing-V2 Header.tsx;
 * the back-end API surface ([@/lib/api/auth], RSA encryption, captcha,
 * Google OAuth, localStorage token storage) is untouched.
 *
 * The dialog body is wrapped in `.landing-v2` so the dark theme tokens
 * (background/foreground/muted/border) match the surrounding Nav.
 */
export function LoginDialog({ open, onOpenChange, onSuccess, initialMode = "signin" }: LoginDialogProps) {
  const { language, t } = useLanguage();
  const [mode, setMode] = useState<Mode>(initialMode);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // 邀请码状态
  const [inviteCode, setInviteCode] = useState("");
  const [inviteCodeError, setInviteCodeError] = useState<string | null>(null);
  const [isValidatingInviteCode, setIsValidatingInviteCode] = useState(false);
  const inviteValidationSeqRef = useRef(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [codeCountdown, setCodeCountdown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const clearInviteCodeField = useCallback(() => {
    inviteValidationSeqRef.current += 1;
    setInviteCode("");
    setInviteCodeError(null);
    setIsValidatingInviteCode(false);
    clearInviteCode();
  }, []);

  const validateInviteCodeForRegister = useCallback(
    async (rawCode: string): Promise<{ canSubmit: boolean; inviteCode: string }> => {
      const code = normalizeInviteCode(rawCode);
      if (!code) {
        setInviteCodeError(null);
        return { canSubmit: true, inviteCode: "" };
      }

      const seq = inviteValidationSeqRef.current + 1;
      inviteValidationSeqRef.current = seq;
      setIsValidatingInviteCode(true);

      try {
        const response = await validateRegisterInviteCode(code);
        if (seq !== inviteValidationSeqRef.current) {
          return { canSubmit: true, inviteCode: code };
        }

        const result = response.data;
        if (result && !result.valid && result.shouldClearInviteCode) {
          clearInviteCodeField();
          setIsValidatingInviteCode(false);
          setInviteCodeError(t("auth.invite.invalid"));
          return { canSubmit: false, inviteCode: "" };
        }

        const normalizedCode = normalizeInviteCode(result?.inviteCode || code);
        setInviteCodeError(null);
        if (normalizedCode && normalizedCode !== code) {
          setInviteCode(normalizedCode);
        }
        if (normalizedCode) {
          saveInviteCode(normalizedCode);
        }
        return { canSubmit: true, inviteCode: normalizedCode };
      } catch {
        if (seq === inviteValidationSeqRef.current) {
          setInviteCodeError(null);
        }
        return { canSubmit: true, inviteCode: code };
      } finally {
        if (seq === inviteValidationSeqRef.current) {
          setIsValidatingInviteCode(false);
        }
      }
    },
    [clearInviteCodeField, t]
  );

  const showRegisterSuccessToast = useCallback(
    (result: RegisterResult | null) => {
      const credits = Number(result?.grantedCredits || 0);
      if (credits > 0) {
        toast.success(t("auth.toast.registerCredits").replace("{credits}", String(credits)));
        return;
      }
      toast.success(t("auth.toast.registerSuccess"));
    },
    [t]
  );

  // Reset form state whenever the dialog re-opens.
  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setVerificationCode("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setError(null);
    setCodeCountdown(0);
    setInviteCodeError(null);
    setIsValidatingInviteCode(false);
    inviteValidationSeqRef.current += 1;
    // 从 sessionStorage 恢复邀请码
    const stored = normalizeInviteCode(getInviteCode());
    if (stored) {
      setInviteCode(stored);
      if (initialMode === "signup") {
        void validateInviteCodeForRegister(stored);
      }
    } else {
      setInviteCode("");
    }
  }, [open, initialMode, validateInviteCodeForRegister]);

  const handleSendCode = async () => {
    if (!email || !email.trim()) {
      setError(t("auth.error.emailRequired"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(t("auth.error.emailInvalid"));
      return;
    }
    if (codeCountdown > 0) return;

    setIsSendingCode(true);
    setError(null);
    try {
      await sendCaptcha({ email: email.trim() });
      setCodeCountdown(60);
      const id = setInterval(() => {
        setCodeCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(id);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.error.sendCodeFailed"));
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setIsGoogleLoading(true);
    try {
      const res = await getGoogleOAuthUrl();
      if (res.success && res.data?.authorizeUrl) {
        // 将邀请码保存到 sessionStorage，OAuth 回调页会读取
        const code = inviteCode.trim();
        if (code) {
          saveInviteCode(code);
        }
        window.location.href = res.data.authorizeUrl;
        return;
      }
      setError(t("auth.error.googleUrlFailed"));
    } catch {
      setError(t("auth.error.googleLoginFailed"));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const finalizeAuthSuccess = async (token: string) => {
    saveToken(token);
    const userInfoResponse = await getUserInfo();
    saveUserInfo(userInfoResponse.data);
    // 登录/注册成功后清除邀请码
    clearInviteCode();
    onSuccess?.(userInfoResponse.data);
    onOpenChange(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      const encryptedPassword = await encryptPassword(password);
      const response = await login({
        clientId: "portal-a",
        authType: "EMAIL_PASSWORD",
        email: email.trim(),
        password: encryptedPassword,
      });
      await finalizeAuthSuccess(response.data.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.error.loginFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError(t("auth.error.passwordMismatch"));
      return;
    }
    if (!verificationCode.trim()) {
      setError(t("auth.error.verificationRequired"));
      return;
    }
    setIsLoading(true);
    try {
      const encryptedPassword = await encryptPassword(password);
      const inviteValidation = await validateInviteCodeForRegister(inviteCode);
      if (!inviteValidation.canSubmit) {
        return;
      }

      const registerResponse = await register({
        email: email.trim(),
        password: encryptedPassword,
        captcha: verificationCode.trim(),
        inviteCode: inviteValidation.inviteCode || '',
      });

      // Only proceed with login if registration was successful
      if (!registerResponse.success || !registerResponse.data?.success) {
        clearInviteCodeField();
        setError(registerResponse.msg || t("auth.error.registerFailed"));
        return;
      }

      // Auto sign-in right after register so the user lands in an authed state.
      const loginResponse = await login({
        clientId: "portal-a",
        authType: "EMAIL_PASSWORD",
        email: email.trim(),
        password: encryptedPassword,
      });
      await finalizeAuthSuccess(loginResponse.data.token);
      showRegisterSuccessToast(registerResponse.data);
    } catch (err) {
      clearInviteCodeField();
      setError(err instanceof Error ? err.message : t("auth.error.registerFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) return setError(t("auth.error.emailRequired"));
    if (!emailRegex.test(email.trim())) return setError(t("auth.error.emailInvalid"));
    if (!verificationCode.trim()) return setError(t("auth.error.codeRequiredForgot"));
    if (!password) return setError(t("auth.error.newPasswordRequired"));
    if (password.length < 6) return setError(t("auth.error.passwordMin"));
    if (password !== confirmPassword) return setError(t("auth.error.passwordMismatchForgot"));

    setIsLoading(true);
    try {
      const encryptedPassword = await encryptPassword(password);
      await forgotPassword({
        email: email.trim(),
        password: encryptedPassword,
        captcha: verificationCode.trim(),
      });
      setMode("signin");
      setPassword("");
      setConfirmPassword("");
      setVerificationCode("");
      toast.success(t("auth.toast.passwordResetOk"));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("auth.error.resetFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const termsBlock = (
    <p className="text-xs text-center text-muted-foreground">
      {t("auth.terms.prefix")}
      <a
        href={`https://policies.oran.cn/terms?lang=${language}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-foreground font-medium hover:underline"
      >
        {t("auth.terms.service")}
      </a>
      {t("auth.terms.mid")}
      <a
        href={`https://policies.oran.cn/privacy?lang=${language}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-foreground font-medium hover:underline"
      >
        {t("auth.terms.privacy")}
      </a>
      。
    </p>
  );

  const errorBox = error ? (
    <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm">
      {error}
    </div>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="landing-v2 sm:max-w-[420px] bg-background border border-border rounded-3xl p-8 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0">
        {mode === "forgot" ? (
          <div className="space-y-6">
            <DialogTitle className="text-xl font-semibold text-center text-foreground">
              {t("auth.forgot.title")}
            </DialogTitle>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              {errorBox}

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">{t("auth.label.email")}</label>
                <Input
                  type="email"
                  placeholder={t("auth.placeholder.email")}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError(null);
                  }}
                  className="h-12 rounded-2xl bg-muted/50 border-0 px-4 focus-visible:ring-0 focus-visible:ring-offset-0"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">{t("auth.label.code")}</label>
                <div className="flex gap-3">
                  <Input
                    type="text"
                    placeholder={t("auth.placeholder.code")}
                    value={verificationCode}
                    onChange={(e) => {
                      setVerificationCode(e.target.value);
                      setError(null);
                    }}
                    className="h-12 rounded-2xl bg-muted/50 border-0 px-4 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0"
                    required
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleSendCode}
                    className="h-12 px-4 hover:bg-transparent font-medium whitespace-nowrap rounded-2xl bg-muted/50"
                    disabled={isLoading || isSendingCode || codeCountdown > 0}
                  >
                    {isSendingCode
                      ? t("auth.sendCode.sending")
                      : codeCountdown > 0
                        ? `${codeCountdown}${t("auth.sendCode.seconds")}`
                        : t("auth.sendCode")}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">{t("auth.label.newPassword")}</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("auth.placeholder.password")}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                    className="h-12 rounded-2xl bg-muted/50 border-0 px-4 pr-12 focus-visible:ring-0 focus-visible:ring-offset-0"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">{t("auth.label.confirmPassword")}</label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t("auth.placeholder.confirm")}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError(null);
                    }}
                    className="h-12 rounded-2xl bg-muted/50 border-0 px-4 pr-12 focus-visible:ring-0 focus-visible:ring-offset-0"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                {t("auth.alreadyHaveAccount")}{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("signin");
                    setError(null);
                  }}
                  className="text-foreground hover:underline font-medium"
                >
                  {t("auth.loginCta")}
                </button>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-2xl bg-foreground text-background hover:bg-foreground/90 font-medium"
                disabled={isLoading}
              >
                {isLoading ? t("auth.resetting") : t("auth.resetPassword")}
              </Button>
            </form>
            {termsBlock}
          </div>
        ) : mode === "signup" ? (
          <div className="space-y-6">
            <DialogTitle className="text-xl font-semibold text-foreground">
              {t("auth.signup.title")}
            </DialogTitle>
            <form onSubmit={handleSignUp} className="space-y-4">
              {errorBox}

              <Input
                type="email"
                placeholder={t("auth.placeholder.email")}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                className="h-12 rounded-2xl bg-muted/50 border-0 px-4 focus-visible:ring-0 focus-visible:ring-offset-0"
                required
                disabled={isLoading}
              />

              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={t("auth.placeholder.password")}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  className="h-12 rounded-2xl bg-muted/50 border-0 px-4 pr-12 focus-visible:ring-0 focus-visible:ring-offset-0"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>

              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t("auth.placeholder.confirmSignup")}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError(null);
                  }}
                  className="h-12 rounded-2xl bg-muted/50 border-0 px-4 pr-12 focus-visible:ring-0 focus-visible:ring-offset-0"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>

              <div className="flex gap-3">
                <Input
                  type="text"
                  placeholder={t("auth.placeholder.code")}
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e.target.value);
                    setError(null);
                  }}
                  className="h-12 rounded-2xl bg-muted/50 border-0 px-4 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0"
                  required
                  disabled={isLoading}
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleSendCode}
                  className="h-12 px-4 hover:bg-transparent font-medium whitespace-nowrap"
                  disabled={isLoading || isSendingCode || codeCountdown > 0}
                >
                  {isSendingCode
                    ? t("auth.sendCode.sending")
                    : codeCountdown > 0
                      ? `${codeCountdown}${t("auth.sendCode.seconds")}`
                      : t("auth.sendCode")}
                </Button>
              </div>

              <Input
                type="text"
                placeholder={t("auth.placeholder.inviteCode")}
                value={inviteCode}
                maxLength={64}
                onChange={(e) => {
                  inviteValidationSeqRef.current += 1;
                  setIsValidatingInviteCode(false);
                  setInviteCode(e.target.value.slice(0, 64));
                  setInviteCodeError(null);
                  setError(null);
                }}
                onBlur={() => {
                  const code = normalizeInviteCode(inviteCode);
                  if (code) {
                    void validateInviteCodeForRegister(code);
                  }
                }}
                className="h-12 rounded-2xl bg-muted/50 border-0 px-4 focus-visible:ring-0 focus-visible:ring-offset-0"
                disabled={isLoading || isValidatingInviteCode}
              />
              {inviteCodeError ? (
                <p className="text-xs text-destructive">{inviteCodeError}</p>
              ) : null}

              <div className="text-sm text-muted-foreground">
                {t("auth.alreadyHaveAccountAlt")}{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("signin");
                    setError(null);
                  }}
                  className="text-foreground hover:underline font-medium"
                >
                  {t("auth.loginCta")}
                </button>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-2xl bg-foreground text-background hover:bg-foreground/90 font-medium"
                disabled={isLoading}
              >
                {isLoading ? t("auth.signup.submitting") : t("auth.signup.cta")}
              </Button>
            </form>
            {termsBlock}
          </div>
        ) : (
          <div className="space-y-6">
            <DialogTitle className="text-xl font-semibold text-center text-foreground">
              {t("auth.login.title")}
            </DialogTitle>

            <Button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading}
              className="w-full h-12 rounded-2xl bg-foreground text-background hover:bg-foreground/90 font-medium"
            >
              <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {isGoogleLoading ? t("auth.google.redirecting") : t("auth.google.continue")}
            </Button>

            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-sm text-muted-foreground">{t("common.or")}</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <p className="text-foreground font-medium">{t("auth.continueEmail")}</p>

            <form onSubmit={handleSignIn} className="space-y-4">
              {errorBox}

              <Input
                type="email"
                placeholder={t("auth.placeholder.email")}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                className="h-12 rounded-2xl bg-muted/50 border-0 px-4 focus-visible:ring-0 focus-visible:ring-offset-0"
                required
                disabled={isLoading}
              />

              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={t("auth.placeholder.password")}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  className="h-12 rounded-2xl bg-muted/50 border-0 px-4 pr-12 focus-visible:ring-0 focus-visible:ring-offset-0"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-2xl bg-foreground text-background hover:bg-foreground/90 font-medium"
                disabled={isLoading}
              >
                {isLoading ? t("auth.login.loggingIn") : t("auth.login.submit")}
              </Button>
            </form>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setMode("signup");
                  setError(null);
                }}
                className="flex-1 h-12 rounded-2xl bg-muted/50 hover:bg-muted font-medium"
              >
                {t("auth.signUpLink")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setMode("forgot");
                  setError(null);
                }}
                className="flex-1 h-12 rounded-2xl bg-muted/50 hover:bg-muted font-medium"
              >
                {t("auth.forgotLink")}
              </Button>
            </div>

            {termsBlock}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default LoginDialog;
